import type { Ancora, Caractere, Celula, Encaixe, PalavraColocada } from '../tipos';
import type { Semente } from '../aleatorio/rng';
import { derivar, proximo } from '../aleatorio/rng';
import { TAMANHO_MAX_JOGADOR, ehHifen, mesmaLetra } from '../lexico/alfabeto';
import type { IndiceLexico } from '../lexico/indice';
import { caracteresCanonicos } from '../lexico/normalizacao';
import { ocorrenciasDaAncora } from '../lexico/validacao';
import { verificarColocacao } from './colisao';
import { celulaNoIndice, origemPeloCruzamento } from './coordenadas';
import type { Grade } from './grade';

/**
 * Pontuação espacial e escolha de encaixe (§9, T05-05, T06-04).
 *
 * Nada aqui sorteia nada: a pontuação é determinística e a semente entra apenas
 * para decidir a *preferência visual* (acima ou abaixo), que dá ritmo ao
 * tabuleiro sem tornar o resultado imprevisível.
 */

export type PreferenciaVisual = 'acima' | 'abaixo';

/**
 * Alterna o lado preferido das sugestões ao longo da palavra-base (§14).
 * Determinística: a mesma coluna, na mesma sessão, sempre prefere o mesmo lado.
 */
export function preferenciaVisual(sementeSessao: Semente, indiceNaBase: number): PreferenciaVisual {
  const s = derivar(sementeSessao, 'lado', indiceNaBase);
  return proximo(s).valor < 0.5 ? 'acima' : 'abaixo';
}

/**
 * Pontua onde a letra de cruzamento cai dentro da sugestão vertical.
 *
 * `indiceNoTexto = 0` faz a palavra descer inteira a partir da base;
 * `indiceNoTexto = tamanho - 1` faz subir inteira. A preferência mira um ponto
 * a 1/4 ou 3/4 do texto para que a sugestão não fique nem colada nem centrada
 * demais. Retorna [0, 1], maior é melhor.
 */
export function pontuarPosicaoVertical(
  indiceNoTexto: number,
  tamanho: number,
  preferencia: PreferenciaVisual,
): number {
  if (tamanho <= 1) return 1;
  const alvo = preferencia === 'abaixo' ? (tamanho - 1) * 0.25 : (tamanho - 1) * 0.75;
  const distancia = Math.abs(indiceNoTexto - alvo) / (tamanho - 1);
  return 1 - distancia;
}

/**
 * Pontua um encaixe do draft ancorado. Prefere manter a âncora perto do centro
 * da palavra, o que deixa a cadeia visualmente equilibrada e reduz a chance de
 * colidir com o que já está no tabuleiro. Retorna [0, 1], maior é melhor.
 */
export function pontuarEncaixe(indiceNoDraft: number, tamanho: number): number {
  if (tamanho <= 1) return 1;
  const centro = (tamanho - 1) / 2;
  return 1 - Math.abs(indiceNoDraft - centro) / centro;
}

/**
 * Todos os encaixes possíveis do draft sobre a âncora, do melhor para o pior.
 *
 * Um encaixe inválido continua na lista, marcado com `valido: false`: T06-04
 * exige indicar a ocorrência ativa e permitir alternar, então a interface
 * precisa saber que a alternativa existe mesmo quando ela não cabe.
 */
export function encaixesDoDraft(
  grade: Grade,
  draft: string,
  ancora: Ancora,
  indice: IndiceLexico,
): readonly Encaixe[] {
  const letras = caracteresCanonicos(draft);
  const ocorrencias = ocorrenciasDaAncora(draft, ancora.caractere);

  const encaixes: Encaixe[] = ocorrencias.map((indiceNoDraft) => {
    const origem = origemPeloCruzamento(ancora.celula, 'horizontal', indiceNoDraft);
    const resultado = verificarColocacao(grade, letras, origem, 'horizontal', indice, {
      celula: ancora.celula,
      indiceNoTexto: indiceNoDraft,
    });
    // O motivo é guardado, e não descartado: §7.3 precisa dele para dizer ao
    // jogador por que a palavra não coube, em vez de só que não coube.
    return {
      indiceNoDraft,
      origem,
      pontuacao: pontuarEncaixe(indiceNoDraft, letras.length),
      valido: resultado.ok,
      ...(resultado.ok
        ? {}
        : {
            motivo: resultado.motivo,
            ...(resultado.formada === undefined ? {} : { formada: resultado.formada }),
          }),
    };
  });

  return [...encaixes].sort((a, b) => {
    if (a.valido !== b.valido) return a.valido ? -1 : 1;
    if (b.pontuacao !== a.pontuacao) return b.pontuacao - a.pontuacao;
    // Desempate estável: a ocorrência mais à esquerda.
    return a.indiceNoDraft - b.indiceNoDraft;
  });
}

/** Linhas acima e abaixo da faixa que precisam estar livres para o preview. */
const CORREDOR_PREVIEW = 3;

/** Até que distância da âncora a faixa procura um corredor livre. */
const AFASTAMENTO_MAX_PREVIEW = 8;

/**
 * Onde a faixa flutuante de T05 fica enquanto o draft não contém a âncora
 * (§5.11). Ela se prende à célula escolhida, a `afastamento` colunas de
 * distância — à direita por padrão, à esquerda quando a direita está tomada,
 * e mais longe quando os dois lados estão. "Livre" aqui é um corredor: a faixa
 * inteira mais `CORREDOR_PREVIEW` linhas acima e abaixo, porque as sugestões
 * provisórias sobem e descem a partir dela.
 *
 * A faixa não ocupa células permanentes (T05-02); o objetivo é só não desenhar
 * o preview por cima de palavras confirmadas. Quando nada está livre, fica à
 * direita mesmo.
 */
export function origemPreview(
  grade: Grade,
  ancora: Celula,
  tamanhoDraft: number,
  afastamento: number,
): Celula {
  const largura = Math.max(1, tamanhoDraft);
  const { linha } = ancora;

  const corredorLivre = (origem: Celula): boolean => {
    for (let dl = -CORREDOR_PREVIEW; dl <= CORREDOR_PREVIEW; dl += 1) {
      for (let i = -1; i <= largura; i += 1) {
        if (grade.ocupada({ coluna: origem.coluna + i, linha: linha + dl })) return false;
      }
    }
    return true;
  };

  for (let distancia = afastamento; distancia <= AFASTAMENTO_MAX_PREVIEW; distancia += 1) {
    const direita: Celula = { coluna: ancora.coluna + distancia, linha };
    if (corredorLivre(direita)) return direita;
    const esquerda: Celula = { coluna: ancora.coluna - distancia - largura + 1, linha };
    if (corredorLivre(esquerda)) return esquerda;
  }

  return { coluna: ancora.coluna + afastamento, linha };
}

/** Melhor encaixe válido, ou `undefined` quando nenhuma ocorrência cabe. */
export function melhorEncaixe(
  grade: Grade,
  draft: string,
  ancora: Ancora,
  indice: IndiceLexico,
): Encaixe | undefined {
  return encaixesDoDraft(grade, draft, ancora, indice).find((e) => e.valido);
}

/** Quantas letras a horizontal futura pode ter de cada lado de uma célula. */
export interface Folga {
  readonly esquerda: number;
  readonly direita: number;
}

/** Nenhuma palavra cabe passando por ali. */
export const SEM_FOLGA: Folga = { esquerda: -1, direita: -1 };

/**
 * Espaço livre para uma palavra horizontal que passe por `celula` (§9).
 *
 * Conta, para cada lado, até onde a palavra pode se estender sem ocupar célula
 * cheia, sem encostar de lado em palavra existente (interseção acidental) e
 * mantendo o respiro na ponta. `celula` é o cruzamento e por isso não é
 * examinada — quem a ocupa é a ponte.
 *
 * Espelha `verificarColocacao` para a orientação horizontal; se as duas
 * discordarem, o jogo oferece âncoras que não aceitam palavra alguma.
 */
export function folgaHorizontal(grade: Grade, celula: Celula, limite: number): Folga {
  const livre = (coluna: number): boolean => !grade.ocupada({ coluna, linha: celula.linha });

  const cabeLetra = (coluna: number): boolean =>
    livre(coluna) &&
    !grade.ocupada({ coluna, linha: celula.linha - 1 }) &&
    !grade.ocupada({ coluna, linha: celula.linha + 1 });

  const alcance = (passo: -1 | 1): number => {
    let maximo = -1;
    // `i = 0` é a palavra que começa (ou termina) na própria âncora: basta o
    // respiro do lado de fora.
    for (let i = 0; i < limite; i += 1) {
      if (i > 0 && !cabeLetra(celula.coluna + passo * i)) break;
      if (livre(celula.coluna + passo * (i + 1))) maximo = i;
    }
    return maximo;
  };

  return { esquerda: alcance(-1), direita: alcance(1) };
}

/**
 * Letras de uma ponte que podem virar a próxima âncora (T04-03, T04-05, §9).
 *
 * Ficam de fora: hifens, a interseção já usada, e — última regra de §9 — as
 * letras onde nenhuma palavra do dicionário caberia. Sem essa checagem
 * espacial, uma letra é oferecida só por existir no léxico e o jogador escolhe
 * uma célula onde tudo o que digitar será recusado.
 */
export function ancorasElegiveis(
  ponte: PalavraColocada,
  intersecaoAnterior: Celula | undefined,
  indice: IndiceLexico,
  grade: Grade,
): readonly Ancora[] {
  const letras = caracteresCanonicos(ponte.texto);
  const saida: Ancora[] = [];

  for (let i = 0; i < letras.length; i += 1) {
    const letra = letras[i];
    if (letra === undefined || ehHifen(letra)) continue;

    const celula = celulaNoIndice(ponte.origem, ponte.orientacao, i);
    if (
      intersecaoAnterior !== undefined &&
      celula.coluna === intersecaoAnterior.coluna &&
      celula.linha === intersecaoAnterior.linha
    ) {
      continue;
    }

    if (!indice.temPalavraComLetra(letra)) continue;

    const folga = folgaHorizontal(grade, celula, TAMANHO_MAX_JOGADOR);
    if (!indice.cabeComFolga(letra, folga.esquerda, folga.direita)) continue;

    saida.push({ celula, caractere: letra, indiceNaPonte: i, idPonte: ponte.id });
  }

  return saida;
}

/**
 * Última regra de §9: a colocação não pode deixar a ponte sem nenhuma âncora
 * capaz de sustentar a próxima horizontal.
 *
 * `grade` é a de antes da ponte entrar, e isso é correto: a ponte é vertical,
 * então ela não ocupa nenhuma célula que a horizontal futura possa querer —
 * a única que compartilham é o próprio cruzamento, que `folgaHorizontal` não
 * examina.
 */
export function sustentaProximaHorizontal(
  ponte: PalavraColocada,
  intersecaoAnterior: Celula | undefined,
  indice: IndiceLexico,
  grade: Grade,
): boolean {
  return ancorasElegiveis(ponte, intersecaoAnterior, indice, grade).length > 0;
}

/** Uma letra do texto satisfaz a âncora exigida? (T05-06) */
export function satisfazAncora(caractere: Caractere, ancora: Ancora): boolean {
  return mesmaLetra(caractere, ancora.caractere);
}
